import { CustomersService } from '@/api/services/customers.service';
import { PermissionType } from '@/enums/PermissionType';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpTool,
} from '../tool-types';
import {
  getLimitArg,
  getOptionalBoolean,
  getOptionalEnum,
  getOptionalNumber,
  getOptionalString,
  getPageArg,
} from '../tool-args';
import { runToolApiCall } from '../tool-http';
import { ensurePermissionOrThrow } from '../tool-scope';
import { sanitizeCustomerRow } from '../sanitize';

/**
 * canViewCustomers = OrdersManage — byte-identical to the Customers page nav
 * and route gate (the CustomersView/Manage bits are absent from the admin
 * PermissionType enum; no production role holds them).
 */
const ensureCustomersAccess = (deps: ICartadigitalAdminHostToolDeps) =>
  ensurePermissionOrThrow({
    deps,
    permission: PermissionType.OrdersManage,
    message: 'No tiene permisos para ver clientes',
  });

const CUSTOMER_SORT_FIELDS = ['name', 'nativeId', 'balance', 'createdAt'] as const;

export const customersTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'customers_find',
      description:
        "Search or list the restaurant's saved CUSTOMERS (the 'Clientes' page): client records with account balances ('Saldo' = store credit spendable via the CustomerAccount payment method), tax/invoicing data and delivery contact info. These are NOT club loyalty members (use club_findMembers) and NOT ad-hoc order buyers (use orders_find). Restaurant-wide (ignores the selected branch).",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          search: {
            type: 'string',
            description:
              'Matches name, phone, email, address, custom id (partial) and tax id / customer number N° (exact).',
          },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
          sortBy: {
            type: 'string',
            enum: ['name', 'nativeId', 'balance', 'createdAt'],
            default: 'name',
          },
          sortDesc: { type: 'boolean', default: false },
        },
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewCustomers,
    async execute(args, { deps, scope }) {
      ensureCustomersAccess(deps);

      const search = getOptionalString(args, 'search');
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 20, max: 50 });
      const sortBy =
        getOptionalEnum(args, 'sortBy', CUSTOMER_SORT_FIELDS) ?? 'name';
      const sortDesc = getOptionalBoolean(args, 'sortDesc') ?? false;

      const data = await runToolApiCall(() =>
        CustomersService.getAll({
          restaurantId: scope.restaurantId,
          options: {
            page,
            itemsPerPage: limit,
            search: search ?? undefined,
            sortBy: [sortBy],
            sortDesc: [sortDesc],
            mustSort: true,
          },
        }),
      );

      return {
        total: data.total,
        page,
        results: (data.items ?? []).map(sanitizeCustomerRow),
      };
    },
  },
  {
    definition: {
      name: 'customers_get',
      description:
        "One saved customer's record: balance, tax/invoicing data, contact and delivery info. Resolves customer ids found on orders (orders_get customer.id).",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          customerId: { type: 'number' },
        },
        required: ['customerId'],
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewCustomers,
    async execute(args, { deps, scope }) {
      ensureCustomersAccess(deps);

      const customerIdRaw = getOptionalNumber(args, 'customerId');
      if (customerIdRaw === null || customerIdRaw <= 0) {
        throw new HostToolError('INVALID_ARGS', 'Missing/invalid "customerId"', {
          customerId: args.customerId,
        });
      }
      const customerId = Math.floor(customerIdRaw);

      const customer = await runToolApiCall(() =>
        CustomersService.getOne(customerId),
      );

      // Client tenant fence backing the server-side hardening.
      if (!customer || customer.restaurantId !== scope.restaurantId) {
        throw new HostToolError('NOT_FOUND', 'Cliente no encontrado', {
          customerId,
        });
      }

      return sanitizeCustomerRow(customer);
    },
  },
];
